import React, { useState, useEffect } from 'react';
import { useApi } from '../../../../context/ApiContext/ApiContext';
import { ThemeProvider } from '../../themes/ThemeProvider';
import { processEndpointValues, restructureObject } from '../moduleUtils/apiObjectFormatting';
import FormField from '../baseElements/FormField';
import ModuleContainer from '../baseElements/ModuleContainer';
import ModuleHeader from '../baseElements/ModuleHeader';
import ModuleDescription from '../baseElements/ModuleDescription';
import ModuleBody from '../baseElements/ModuleBody';
import InfoComponent from '../../elements/InfoComponent';
import ButtonComponent from '../../elements/ButtonComponent';
import { moduleConfig } from './SmartplayIngestModuleConfig';

const SmartplayIngest = ({ id, data }) => {
  // API CALL CONTEXT
  const { moduleNodes, registerModule, updateModuleConfig, updateApiParams, setExecutionOrder, toggleModuleActive, state, isExecuting, isCloudProcessRunning } = useApi();
  const [endpointValues, setEndpointValues] = useState(() => {
    return moduleNodes[id] || 
      {
  "endpoint_0": {
    "path": "/ingest/external/jobs",
    "method": "POST",
    "root_url": "https://services.uplynk.com/api/v4",
    "params": {
      "job_type": "live",
      "slicer_id": "smartplay_ingest",    // get value from "Channel Title" formfield - but remove spaces, special character - can use underscore
      "url": "https://c4af3793bf76b33c.mediapackage.us-west-2.amazonaws.com/out/v1/a2a60bf0d27049058a175a3f968ded34/index.m3u8",
      "desc": "smartplay_ingest",
      "persist_segments": true,
      "metadata": {
        "external_id": "",
        "drm_mode": "none",
        "meta": {}
      },
      "state": "SCHEDULED"
    }
  },
  "endpoint_1": {
    "path": "/api2/channel/create",
    "method": "POST",
    "root_url": "https://services.uplynk.com",
    "params": {
      "desc": "Smartplay Ingest",
      "slicer_id": "smartplay_ingest",              // use precisely the same value as slicer_id from endpoint_0
      "require_drm": 0,
      "require_studio_drm": 0
    }
  }
};
});
  const [results, setResults] = useState(null);
  const [isModified, setIsModified] = useState(false);
  const isProcessing = isExecuting || isCloudProcessRunning;

  // Handle form field changes by updating the specific endpoint parameter directly
  const handleChange = (endpointKey, paramName, value) => {
    // Create updates object starting with the current parameter
    const updates = {
      [endpointKey]: {
        params: {
          [paramName]: value
        }
      }
    };
    
    // For description field, generate sanitized ID and propagate to specified endpoints
    if (endpointKey === "endpoint_1" && paramName === "desc" && typeof value === "string") {
      // Sanitize value for ID fields (lowercase, replace special chars with underscores)
      const sanitizedId = value.toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/_+/g, '_');
      
      // Map of target endpoints and their parameters that should receive the sanitized value
      const targetMap = {
        "endpoint_0": ["slicer_id", "desc"],
        "endpoint_1": ["slicer_id"]
      };
      
      // Populate updates for all target endpoints
      Object.entries(targetMap).forEach(([targetEndpoint, params]) => {
        if (!updates[targetEndpoint]) {
          updates[targetEndpoint] = { params: {} };
        }
        
        params.forEach(param => {
          updates[targetEndpoint].params[param] = sanitizedId;
        });
      });
    }
    
    // Apply all updates at once
    setEndpointValues(prev => {
      const newState = { ...prev };
      
      // Apply each endpoint update
      Object.entries(updates).forEach(([endpoint, update]) => {
        newState[endpoint] = {
          ...prev[endpoint],
          params: {
            ...prev[endpoint]?.params,
            ...update.params
          }
        };
      });
      
      return newState;
    });
    
    setIsModified(true);
  };

  // Handle form submission with API context integration
  const handleSubmit = async () => {
    try {
      // Process the endpoint values
      const processedData = processEndpointValues(endpointValues, moduleConfig);
      
      if (!moduleNodes[id]) {
        // Module not registered - register it
        const moduleLabel = data?.label || '';
        const moduleData = {
          ...endpointValues,
          callbacks: moduleConfig.moduleConfig.callbacks,
        };
        registerModule(id, moduleData, moduleLabel, processedData.validation);
      } else {
        // Module exists - update it
        updateModuleConfig(id, processedData.endpoints, processedData.validation);
      }
      
      setIsModified(false);
    } catch (error) {
      console.error('Error processing form:', error);
    }
  };


    // Register module on mount
  useEffect(() => {
    if (!id) return;

    if (!moduleNodes[id]) {
      const moduleLabel = data?.label || '';

      // Process the endpoint values to get validation results
      const processedData = processEndpointValues(endpointValues, moduleConfig);
      
      // Create the full module data including callbacks
      const moduleData = {
        ...endpointValues,
        callbacks: moduleConfig.moduleConfig.callbacks, // Include callbacks from the module config
      };

      // Register module with all data including callbacks
      registerModule(id, moduleData, moduleLabel, processedData.validation);

    } else {
      setEndpointValues(moduleNodes[id]);
    }

    // Clean up when component unmounts
    return () => {
      // If you want to remove this module when the component unmounts
      // removeModule(nodeId);
    };
  }, []);


  useEffect(() => {
    console.log("smartplayIngestModule: ", state);
  }, [state])

  return (
      <ThemeProvider themeKey="uplynk" size="small">
        <ModuleContainer>
          <ModuleHeader title="SmartPlay:Ingest Live" />
          <ModuleDescription description="" />
          <ModuleBody endpoints={moduleConfig.moduleConfig.endpoints} readyState={moduleNodes[id]?.readyState && !isModified} activeState={moduleNodes[id]?.activeState} toggleModuleActive={toggleModuleActive} id={id}>
              {/* Field: job_type for /ingest/external/jobs* */}
              {/* Field: desc for /api2/channel/create */}
                <FormField 
                  type="text"
                  label="Channel Title"
                  value={endpointValues['endpoint_1']?.params?.['desc']}
                  onChange={(value) => handleChange('endpoint_1', 'desc', value)}
                  required={true}
                  placeholder="Sports Channel"
                />

              {/* Field: url for /ingest/external/jobs* */}
              <div className="mt-1">
                <FormField 
                  type="textarea"
                  label="Content Source URL"
                  value={endpointValues['endpoint_0']?.params?.['url']}
                  onChange={(value) => handleChange('endpoint_0', 'url', value)}
                  required={true}
                  placeholder="https://c4af3793bf76b33c.mediapackage.us-west-2.amazonaws.com/out/v1/a2a60bf0d27049058a175a3f968ded34/index.m3u8"
                />
              </div>

              {/* Field: persist_segments for /ingest/external/jobs* */}
                <FormField 
                  type="checkbox"
                  label="Store Segments"
                  value={endpointValues['endpoint_0']?.params?.['persist_segments']}
                  onChange={(value) => handleChange('endpoint_0', 'persist_segments', value)}
                  placeholder="false"
                />

              {/* Field: require_drm for /api2/channel/create - Integer Checkbox */}
                <FormField 
                  type="checkbox"
                  label="Token"
                  value={!!endpointValues['endpoint_1']?.params?.['require_drm']}
                  onChange={(checked) => handleChange('endpoint_1', 'require_drm', checked ? 1 :0)}
                />

              {/* Field: require_studio_drm for /api2/channel/create - Integer Checkbox */}
                <FormField 
                  type="checkbox"
                  label="Studio DRM"
                  value={!!endpointValues['endpoint_1']?.params?.['require_studio_drm']}
                  onChange={(checked) => handleChange('endpoint_1', 'require_studio_drm', checked ? 1 :0)}
                />

              {/* Button: button_1742305595821 */}
              <ButtonComponent 
                id="button_1741528109211"
                label={isProcessing ? "Processing..." : "Apply"}
                variant={isProcessing ? "secondary" : "primary"}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSubmit();
                }}
                readyState={!isProcessing && moduleNodes[id]?.readyState && !isModified}
                executing={isProcessing}
                disabled={isProcessing}
                isProcessing={isProcessing}
              />
          </ModuleBody>
        </ModuleContainer>
      </ThemeProvider>
    );
}

export default SmartplayIngest;